import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2, Building2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import api, { getApiError } from "@/services/api";
import ConsentCheckboxes from "@/components/auth/ConsentCheckboxes";

// Simple debounce implementation to avoid lodash dependency
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const companyRegisterSchema = z
  .object({
    // 기업 정보
    company_name: z.string().min(2, "기업명은 2자 이상이어야 합니다."),
    company_code: z
      .string()
      .min(2, "기업 코드는 2자 이상이어야 합니다.")
      .max(50, "기업 코드는 50자 이하여야 합니다.")
      .regex(
        /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
        "영문 소문자, 숫자, 하이픈만 사용 가능하며, 시작과 끝은 영문 또는 숫자여야 합니다."
      )
      .refine((val) => !val.includes("--"), {
        message: "연속된 하이픈은 사용할 수 없습니다.",
      }),
    business_number: z.string().min(1, "사업자등록번호를 입력해주세요."),
    company_address: z.string().min(1, "기업 주소를 입력해주세요."),
    company_phone: z.string().min(1, "기업 전화번호를 입력해주세요."),
    ceo_name: z.string().min(1, "대표자명을 입력해주세요."),
    industry: z.string().min(1, "업종을 입력해주세요."),

    // 관리자 정보
    admin_name: z.string().min(2, "이름은 2자 이상이어야 합니다."),
    admin_email: z.string().email("유효한 이메일을 입력해주세요."),
    admin_phone: z.string().min(1, "연락처를 입력해주세요."),
    admin_password: z.string(),
    confirmPassword: z.string(),

    // 약관 동의
    agreeToTerms: z.literal(true, {
      errorMap: () => ({ message: "이용약관에 동의해주세요." }),
    }),
    agreeToPrivacy: z.literal(true, {
      errorMap: () => ({ message: "개인정보 수집 및 이용에 동의해주세요." }),
    }),
  });

type CompanyRegisterFormData = z.infer<typeof companyRegisterSchema>;

export default function CompanyRegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [codeAvailability, setCodeAvailability] = useState<{
    available: boolean | null;
    message: string;
  }>({ available: null, message: "" });
  const [emailAvailability, setEmailAvailability] = useState<{
    available: boolean | null;
    message: string;
    existing_user?: boolean;
  }>({ available: null, message: "", existing_user: false });

  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<CompanyRegisterFormData>({
    resolver: zodResolver(companyRegisterSchema),
    defaultValues: {
      agreeToTerms: false as any,
      agreeToPrivacy: false as any,
    },
  });

  const companyCode = watch("company_code");
  const adminEmail = watch("admin_email");
  const companyCodeField = register("company_code");
  const agreeToTerms = watch("agreeToTerms");
  const agreeToPrivacy = watch("agreeToPrivacy");

  // 기업 코드 중복 체크 (디바운스)
  const checkCodeAvailability = debounce(async (code: string) => {
    if (!code || code.length < 2) {
      setCodeAvailability({ available: null, message: "" });
      return;
    }

    // 형식 검증 먼저
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(code)) {
      setCodeAvailability({
        available: false,
        message: "영문 소문자, 숫자, 하이픈만 사용 가능합니다.",
      });
      return;
    }

    // 예약어 체크
    const reservedWords = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'localhost', 'test', 'dev', 'staging', 'production', 'demo'];
    if (reservedWords.includes(code.toLowerCase())) {
      setCodeAvailability({
        available: false,
        message: `'${code}'는 예약어로 사용할 수 없습니다.`,
      });
      return;
    }

    setIsCheckingCode(true);
    try {
      const response = await api.post("/company/check-availability", {
        company_code: code,
      });
      setCodeAvailability({
        available: response.data.available,
        message: response.data.message,
      });
    } catch (error) {
      const apiError = getApiError(error);
      setCodeAvailability({
        available: false,
        message: apiError.message,
      });
    } finally {
      setIsCheckingCode(false);
    }
  }, 500);

  // 이메일 중복 체크 (디바운스)
  const checkEmailAvailability = debounce(async (email: string) => {
    if (!email) {
      setEmailAvailability({ available: null, message: "", existing_user: false });
      return;
    }

    // 이메일 형식 검증 먼저
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setEmailAvailability({
        available: false,
        message: "유효한 이메일 주소를 입력해주세요.",
        existing_user: false,
      });
      return;
    }

    setIsCheckingEmail(true);
    try {
      const response = await api.post("/company/check-email", {
        email: email,
      });
      setEmailAvailability({
        available: response.data.available,
        message: response.data.message,
        existing_user: response.data.existing_user || false,
      });
    } catch (error) {
      const apiError = getApiError(error);
      setEmailAvailability({
        available: false,
        message: apiError.message,
        existing_user: false,
      });
    } finally {
      setIsCheckingEmail(false);
    }
  }, 500);

  // 기업 코드 변경 시 중복 체크
  useState(() => {
    if (companyCode) {
      checkCodeAvailability(companyCode);
    }
  });

  // 이메일 변경 시 중복 체크
  useState(() => {
    if (adminEmail) {
      checkEmailAvailability(adminEmail);
    }
  });

  const onSubmit = async (data: CompanyRegisterFormData) => {
    // 기업 코드 사용 가능 여부 확인
    if (codeAvailability.available === false) {
      toast({
        title: "기업 코드 오류",
        description: "이미 사용 중인 기업 코드입니다.",
        variant: "destructive",
      });
      return;
    }

    // 비밀번호 유효성 검사
    if (emailAvailability.existing_user) {
      // 기존 사용자: 비밀번호 필수 (계정 소유자 확인용)
      if (!data.admin_password) {
        setError("admin_password", { message: "기존 계정의 비밀번호를 입력해주세요." });
        return;
      }
    } else {
      // 신규 사용자: 비밀번호 복잡도 검사
      if (!data.admin_password || data.admin_password.length < 8) {
        setError("admin_password", { message: "비밀번호는 8자 이상이어야 합니다." });
        return;
      }
      const complexityRegex =
        /(?=.*[a-zA-Z])(?=.*\d)|(?=.*[a-zA-Z])(?=.*[!@#$%^&*])|(?=.*\d)(?=.*[!@#$%^&*])/;
      if (!complexityRegex.test(data.admin_password)) {
        setError("admin_password", { message: "영문, 숫자, 특수문자 중 2가지 이상을 조합해야 합니다." });
        return;
      }
      if (data.admin_password !== data.confirmPassword) {
        setError("confirmPassword", { message: "비밀번호가 일치하지 않습니다." });
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = {
        company_name: data.company_name,
        company_code: data.company_code,
        business_number: data.business_number,
        company_address: data.company_address,
        company_phone: data.company_phone,
        ceo_name: data.ceo_name,
        industry: data.industry,
        admin_name: data.admin_name,
        admin_email: data.admin_email,
        admin_phone: data.admin_phone,
        admin_password: data.admin_password,
        agree_to_terms: true,
        agree_to_privacy: true,
      };

      console.log('Sending company registration:', payload);
      const response = await api.post("/company/register", payload);

      const result = response.data;

      toast({
        title: "기업 가입 완료",
        description: result.message,
      });

      // 로그인 페이지로 이동 (서브도메인 정보 표시)
      navigate("/login", {
        state: {
          subdomain: result.subdomain,
          email: result.admin_email,
          message: result.message,
        },
      });
    } catch (error) {
      const apiError = getApiError(error);
      console.error('Company registration error:', error);
      console.error('Error response:', (error as any)?.response?.data);

      toast({
        title: "기업 가입 실패",
        description: apiError.message || JSON.stringify((error as any)?.response?.data),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
        {/* 가입 폼 */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* 헤더 */}
          <div className="text-center mb-2">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-lg mb-3">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">기업 회원가입</h1>
            <p className="text-sm text-gray-600 mt-1">우리 기업의 업무를 시작하세요</p>
          </div>

          {/* 기업 정보 섹션 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">기업 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
            {/* 기업명 */}
            <div>
              <Label htmlFor="company_name">기업명 <span className="text-red-500">*</span></Label>
              <Input
                id="company_name"
                placeholder="(주)기업명"
                className="mt-1.5"
                {...register("company_name")}
              />
              {errors.company_name && (
                <p className="text-sm text-destructive mt-1">{errors.company_name.message}</p>
              )}
            </div>

              {/* 기업 코드 */}
              <div className="md:col-span-3">
                <Label htmlFor="company_code">기업 코드 (서브도메인) <span className="text-red-500">*</span></Label>
                <Input
                  id="company_code"
                  placeholder="mycompany (영문 소문자, 숫자, 하이픈만 가능)"
                  className="mt-1.5"
                  autoCapitalize="none"
                  autoCorrect="off"
                  {...companyCodeField}
                  onChange={(e) => {
                    // 대문자는 소문자로 자동 변환
                    e.target.value = e.target.value.toLowerCase();
                    void companyCodeField.onChange(e);
                  }}
                />
                {errors.company_code && (
                  <p className="text-sm text-destructive mt-1">{errors.company_code.message}</p>
                )}
                {isCheckingCode && (
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    확인 중...
                  </p>
                )}
                {!isCheckingCode && codeAvailability.available !== null && (
                  <p
                    className={`text-sm flex items-center gap-1 mt-1 ${
                      codeAvailability.available ? "text-green-600" : "text-destructive"
                    }`}
                  >
                    {codeAvailability.available ? (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    {codeAvailability.message}
                  </p>
                )}
              </div>

              {/* 사업자등록번호 */}
              <div>
                <Label htmlFor="business_number">사업자등록번호 <span className="text-red-500">*</span></Label>
                <Input
                  id="business_number"
                  placeholder="123-45-67890"
                  className="mt-1.5"
                  {...register("business_number")}
                />
                {errors.business_number && (
                  <p className="text-sm text-destructive mt-1">{errors.business_number.message}</p>
                )}
              </div>

              {/* 대표자명 */}
              <div>
                <Label htmlFor="ceo_name">대표자명 <span className="text-red-500">*</span></Label>
                <Input
                  id="ceo_name"
                  placeholder="홍길동"
                  className="mt-1.5"
                  {...register("ceo_name")}
                />
                <p className="text-xs text-muted-foreground mt-1">공동대표의 경우 쉼표로 구분하여 입력하세요. (예: 홍길동, 김철수)</p>
                {errors.ceo_name && (
                  <p className="text-sm text-destructive mt-1">{errors.ceo_name.message}</p>
                )}
              </div>

              {/* 업종 */}
              <div>
                <Label htmlFor="industry">업종 <span className="text-red-500">*</span></Label>
                <Input
                  id="industry"
                  placeholder="IT 서비스업"
                  className="mt-1.5"
                  {...register("industry")}
                />
                {errors.industry && (
                  <p className="text-sm text-destructive mt-1">{errors.industry.message}</p>
                )}
              </div>

              {/* 기업 전화번호 */}
              <div>
                <Label htmlFor="company_phone">기업 전화번호 <span className="text-red-500">*</span></Label>
                <Input
                  id="company_phone"
                  placeholder="02-1234-5678"
                  className="mt-1.5"
                  {...register("company_phone")}
                />
                {errors.company_phone && (
                  <p className="text-sm text-destructive mt-1">{errors.company_phone.message}</p>
                )}
              </div>

              {/* 기업 주소 */}
              <div className="md:col-span-3">
                <Label htmlFor="company_address">기업 주소 <span className="text-red-500">*</span></Label>
                <Input
                  id="company_address"
                  placeholder="서울특별시 강남구..."
                  className="mt-1.5"
                  {...register("company_address")}
                />
                {errors.company_address && (
                  <p className="text-sm text-destructive mt-1">{errors.company_address.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* 관리자 정보 섹션 */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b">관리자 정보</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">

              {/* 관리자 이름 */}
              <div>
                <Label htmlFor="admin_name">이름 <span className="text-red-500">*</span></Label>
                <Input
                  id="admin_name"
                  placeholder="홍길동"
                  className="mt-1.5"
                  {...register("admin_name")}
                />
                {errors.admin_name && (
                  <p className="text-sm text-destructive mt-1">{errors.admin_name.message}</p>
                )}
              </div>

              {/* 관리자 전화번호 */}
              <div>
                <Label htmlFor="admin_phone">연락처 <span className="text-red-500">*</span></Label>
                <Input
                  id="admin_phone"
                  placeholder="010-1234-5678"
                  className="mt-1.5"
                  {...register("admin_phone")}
                />
                {errors.admin_phone && (
                  <p className="text-sm text-destructive mt-1">{errors.admin_phone.message}</p>
                )}
              </div>

              {/* 관리자 이메일 */}
              <div className="md:col-span-3">
                <Label htmlFor="admin_email">이메일 <span className="text-red-500">*</span></Label>
                <Input
                  id="admin_email"
                  type="email"
                  placeholder="admin@example.com"
                  className="mt-1.5"
                  {...register("admin_email")}
                />
                {errors.admin_email && (
                  <p className="text-sm text-destructive mt-1">{errors.admin_email.message}</p>
                )}
                {isCheckingEmail && (
                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    이메일 확인 중...
                  </p>
                )}
                {!isCheckingEmail && emailAvailability.available === true && !emailAvailability.existing_user && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {emailAvailability.message}
                  </p>
                )}
                {!isCheckingEmail && emailAvailability.available === true && emailAvailability.existing_user && (
                  <p className="text-sm text-amber-600 mt-1 flex items-center gap-1">
                    <XCircle className="h-3.5 w-3.5" />
                    이미 등록된 계정입니다. 아래에서 기존 비밀번호를 입력해야 가입 신청이 가능합니다.
                  </p>
                )}
                {!isCheckingEmail && emailAvailability.available === false && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                    <XCircle className="h-3.5 w-3.5" />
                    {emailAvailability.message}
                  </p>
                )}
              </div>

              {/* 비밀번호 */}
              <div>
                <Label htmlFor="admin_password">비밀번호 <span className="text-red-500">*</span></Label>
                <div className="relative mt-1.5">
                  <Input
                    id="admin_password"
                    type={showPassword ? "text" : "password"}
                    placeholder={
                      emailAvailability.existing_user
                        ? "기존 계정의 비밀번호를 입력해주세요"
                        : "8자 이상 (영문, 숫자, 특수문자 조합)"
                    }
                    className="pr-10"
                    {...register("admin_password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.admin_password && (
                  <p className="text-sm text-destructive mt-1">{errors.admin_password.message}</p>
                )}
                {emailAvailability.existing_user && (
                  <p className="text-xs text-amber-600 mt-1">
                    이미 등록된 계정입니다. 계정 소유자 확인을 위해 기존 비밀번호를 입력해주세요.
                  </p>
                )}
              </div>

              {/* 비밀번호 확인 (신규 사용자만) */}
              {!emailAvailability.existing_user && (
                <div>
                  <Label htmlFor="confirmPassword">비밀번호 확인 <span className="text-red-500">*</span></Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="비밀번호 재입력"
                    className="mt-1.5"
                    {...register("confirmPassword")}
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 약관 동의 */}
          <ConsentCheckboxes
            termsAgreed={agreeToTerms === true}
            privacyAgreed={agreeToPrivacy === true}
            onTermsChange={(checked) => setValue("agreeToTerms", checked as any, { shouldValidate: true })}
            onPrivacyChange={(checked) => setValue("agreeToPrivacy", checked as any, { shouldValidate: true })}
            errors={{
              terms: errors.agreeToTerms?.message,
              privacy: errors.agreeToPrivacy?.message,
            }}
          />

          {/* 제출 버튼 */}
          <div className="pt-6">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || codeAvailability.available === false}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              가입 신청
            </Button>

            {/* 하단 링크 */}
            <div className="text-center text-sm text-gray-600 pt-6 mt-6 border-t">
              이미 계정이 있으신가요?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                로그인
              </Link>
            </div>
          </div>
        </form>
    </div>
  );
}
